import type { LegacyAudioPlaybackFailure, LegacyAudioPlaybackState } from './helpers'

export const ALPHABET_AUDIO_LIMITS = {
  CACHE_BYTES: 12 * 1024 * 1024,
  CACHE_CLIPS: 29,
  ENCODED_BYTES: 1024 * 1024,
  CLIP_SECONDS: 12,
  PREPARE_CLIPS: 29,
  OPERATIONS: 2,
  OPERATION_MS: 8000,
} as const

export interface AlphabetAudioState {
  readonly key: string | null
  readonly playbackState: LegacyAudioPlaybackState
  readonly failureReason: LegacyAudioPlaybackFailure | null
  readonly engine: 'WEB_AUDIO' | 'NATIVE'
}

interface Dependencies {
  readonly createContext?: () => AudioContext | null
  readonly createAudio?: () => HTMLAudioElement
  readonly loadEncoded?: (source: string, signal: AbortSignal) => Promise<ArrayBuffer>
  readonly operationTimeoutMs?: number
  readonly resolveNativeSource?: (source: string) => string
}

interface BufferOperation {
  readonly abort: AbortController
  promise: Promise<AudioBuffer>
}

const ALPHABET = Array.from('AĂÂBCDĐEÊGHIKLMNOÔƠPQRSTUƯVXY')

export function isAlphabetVocabulary(content: readonly { readonly text?: string }[]) {
  return content.length === ALPHABET.length && content.every((item, index) =>
    item.text?.normalize('NFC').replace(/\s/g, '').toLowerCase() === ALPHABET[index].toLowerCase().repeat(2),
  )
}

function abortError() {
  return new DOMException('Audio selection changed', 'AbortError')
}

function withDeadline<T>(task: Promise<T>, abort: AbortController, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const cancelled = () => reject(abortError())
    const timer = setTimeout(() => {
      reject(new Error('Audio preparation timed out'))
      abort.abort()
    }, timeoutMs)
    abort.signal.addEventListener('abort', cancelled, { once: true })
    task.then(resolve, reject)
    // Also detach listeners after cancellation/deadline, even if a decoder stalls.
    const cleanup = () => {
      clearTimeout(timer)
      abort.signal.removeEventListener('abort', cancelled)
    }
    task.then(cleanup, cleanup)
    abort.signal.addEventListener('abort', cleanup, { once: true })
    if (abort.signal.aborted) { cancelled(); cleanup() }
  })
}

function createContext() {
  const Constructor = window.AudioContext ?? (window as Window & {
    webkitAudioContext?: typeof AudioContext
  }).webkitAudioContext
  return Constructor ? new Constructor({ latencyHint: 'interactive' }) : null
}

async function loadEncoded(source: string, signal: AbortSignal) {
  // Bypass a cached no-CORS media response when reading bytes for Web Audio.
  const response = await fetch(source, { signal, mode: 'cors', cache: 'no-store' })
  if (!response.ok) throw new Error('Audio unavailable')
  const size = Number(response.headers.get('content-length'))
  if (size > ALPHABET_AUDIO_LIMITS.ENCODED_BYTES) {
    await response.body?.cancel()
    throw new Error('Audio too large')
  }
  const reader = response.body?.getReader()
  if (!reader) throw new Error('Audio streaming unavailable')
  const chunks: Uint8Array[] = []
  let total = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      total += value.byteLength
      if (total > ALPHABET_AUDIO_LIMITS.ENCODED_BYTES) throw new Error('Audio too large')
      chunks.push(value)
    }
  } finally {
    await reader.cancel().catch(() => undefined)
    reader.releaseLock()
  }
  const bytes = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.byteLength
  }
  return bytes.buffer
}

export class AlphabetAudioPlayer {
  private context: AudioContext | null = null
  private native: HTMLAudioElement | null = null
  private node: AudioBufferSourceNode | null = null
  private readonly cache = new Map<string, AudioBuffer>()
  private readonly nativeSources = new Set<string>()
  private readonly listeners = new Set<(state: AlphabetAudioState) => void>()
  private readonly operations = new Map<string, BufferOperation>()
  private intent = 0
  private disposed = false
  private resumePosition: { key: string; source: string; offset: number } | null = null
  private startedAt = 0
  private resumeAbort: AbortController | null = null
  private state: AlphabetAudioState = { key: null, playbackState: 'idle', failureReason: null, engine: 'WEB_AUDIO' }
  private readonly dependencies: Dependencies

  constructor(dependencies: Dependencies = {}) {
    this.dependencies = dependencies
  }

  subscribe(listener: (state: AlphabetAudioState) => void) {
    this.listeners.add(listener)
    listener(this.state)
    return () => { this.listeners.delete(listener) }
  }

  get cacheUsage() {
    return { clips: this.cache.size, bytes: [...this.cache.values()].reduce((sum, buffer) => sum + this.bufferBytes(buffer), 0) }
  }

  private bufferBytes(buffer: AudioBuffer) {
    return buffer.length * buffer.numberOfChannels * 4
  }

  private getContext() {
    if (!this.context) this.context = (this.dependencies.createContext ?? createContext)()
    return this.context
  }

  private publish(state: AlphabetAudioState) {
    if (this.disposed) return
    this.state = state
    this.listeners.forEach((listener) => listener(state))
  }

  private async buffer(source: string, preparing = false) {
    const cached = this.cache.get(source)
    if (cached) {
      this.cache.delete(source)
      this.cache.set(source, cached)
      return cached
    }
    const pending = this.operations.get(source)
    if (pending) return pending.promise
    if (this.disposed) throw abortError()
    if (this.operations.size >= ALPHABET_AUDIO_LIMITS.OPERATIONS) throw new Error('Audio preparation busy')
    const context = this.getContext()
    if (!context) throw new Error('Web Audio unavailable')
    const abort = new AbortController()
    const operation: BufferOperation = {
      abort,
      promise: Promise.resolve(null as unknown as AudioBuffer),
    }
    const task = (async () => {
      const bytes = await (this.dependencies.loadEncoded ?? loadEncoded)(source, abort.signal)
      if (abort.signal.aborted) throw abortError()
      if (bytes.byteLength > ALPHABET_AUDIO_LIMITS.ENCODED_BYTES) throw new Error('Audio too large')
      const decoded = await context.decodeAudioData(bytes)
      if (abort.signal.aborted || this.disposed) throw abortError()
      const size = this.bufferBytes(decoded)
      if (decoded.duration > ALPHABET_AUDIO_LIMITS.CLIP_SECONDS || size > ALPHABET_AUDIO_LIMITS.CACHE_BYTES) {
        throw new Error('Audio exceeds short-clip budget')
      }
      // Background warming must not evict the first ready letters to warm the last.
      if (preparing && (this.cache.size >= ALPHABET_AUDIO_LIMITS.CACHE_CLIPS || this.cacheUsage.bytes + size > ALPHABET_AUDIO_LIMITS.CACHE_BYTES)) {
        throw new RangeError('Audio preparation budget reached')
      }
      while (this.cache.size >= ALPHABET_AUDIO_LIMITS.CACHE_CLIPS || this.cacheUsage.bytes + size > ALPHABET_AUDIO_LIMITS.CACHE_BYTES) {
        const oldest = this.cache.keys().next().value
        if (oldest === undefined) break
        this.cache.delete(oldest)
      }
      this.cache.set(source, decoded)
      return decoded
    })()
    operation.promise = withDeadline(task, abort, this.dependencies.operationTimeoutMs ?? ALPHABET_AUDIO_LIMITS.OPERATION_MS)
    this.operations.set(source, operation)
    // A decoder cannot be aborted. Keep its slot until it really settles, so
    // repeated taps cannot accumulate an unbounded number of stalled decoders.
    const release = () => { if (this.operations.get(source) === operation) this.operations.delete(source) }
    task.then(release, release)
    return operation.promise
  }

  async prepare(sources: readonly string[]) {
    try {
      if (!this.getContext()) return
    } catch { return }
    for (const source of [...new Set(sources)].slice(0, ALPHABET_AUDIO_LIMITS.PREPARE_CLIPS)) {
      if (this.disposed) return
      if (this.operations.size >= ALPHABET_AUDIO_LIMITS.OPERATIONS) {
        await Promise.race([...this.operations.values()].map((operation) => operation.promise)).catch(() => undefined)
        if (this.operations.size >= ALPHABET_AUDIO_LIMITS.OPERATIONS) return
      }
      try { await this.buffer(source, true) } catch (error) {
        if (error instanceof RangeError) return
        if (!(error instanceof DOMException && error.name === 'AbortError')) this.nativeSources.add(source)
      }
    }
  }

  private stop(preservePosition = false) {
    this.intent += 1
    this.resumeAbort?.abort()
    this.resumeAbort = null
    // Selection changes stop sound, not the bounded preparation for this grid.
    if (preservePosition && this.resumePosition) {
      this.resumePosition.offset = this.state.engine === 'NATIVE'
        ? this.native?.currentTime ?? 0
        : this.resumePosition.offset + (this.node && this.context ? this.context.currentTime - this.startedAt : 0)
    } else {
      this.resumePosition = null
    }
    if (this.node) {
      this.node.onended = null
      this.node.stop()
      this.node.disconnect()
      this.node = null
    }
    this.native?.pause()
    this.publish({ ...this.state, key: null, playbackState: 'idle', failureReason: null })
  }

  async toggle(key: string, source: string) {
    if (this.disposed) return
    if (this.state.key === key && (this.state.playbackState === 'starting' || this.state.playbackState === 'playing')) {
      this.stop(true)
      return
    }
    const previous = this.resumePosition
    this.stop(false)
    this.resumePosition = previous?.key === key && previous.source === source ? previous : { key, source, offset: 0 }
    const intent = this.intent
    let context: AudioContext | null = null
    try { context = this.getContext() } catch { /* Native fallback stays in this gesture. */ }
    if (!context || this.nativeSources.has(source) || (!this.cache.has(source) && !this.operations.has(source) && this.operations.size >= ALPHABET_AUDIO_LIMITS.OPERATIONS)) {
      return this.playNative(key, source, intent)
    }
    this.publish({ key, playbackState: 'starting', failureReason: null, engine: 'WEB_AUDIO' })
    try {
      // Invoke resume synchronously inside the click/key gesture, before fetching.
      const resumed = context.resume()
      const resumeAbort = this.resumeAbort = new AbortController()
      const [buffer] = await Promise.all([
        this.buffer(source),
        withDeadline(resumed, resumeAbort, this.dependencies.operationTimeoutMs ?? ALPHABET_AUDIO_LIMITS.OPERATION_MS),
      ])
      if (this.disposed || this.intent !== intent) return
      const node = context.createBufferSource()
      node.buffer = buffer
      node.connect(context.destination)
      const offset = (this.resumePosition?.offset ?? 0) < buffer.duration ? this.resumePosition?.offset ?? 0 : 0
      this.resumePosition = { key, source, offset }
      this.startedAt = context.currentTime
      node.onended = () => {
        node.disconnect()
        if (this.intent !== intent || this.disposed) return
        this.node = null
        this.resumePosition = null
        this.publish({ key: null, playbackState: 'idle', failureReason: null, engine: 'WEB_AUDIO' })
      }
      try { node.start(0, offset) } catch (error) { node.disconnect(); throw error }
      this.node = node
      this.publish({ key, playbackState: 'playing', failureReason: null, engine: 'WEB_AUDIO' })
    } catch (error) {
      if (this.intent !== intent || this.disposed) return
      // A fallback after async failure needs a fresh user gesture on iOS.
      this.nativeSources.add(source)
      this.publish({ key, playbackState: 'failed', failureReason: error instanceof DOMException && error.name === 'NotAllowedError' ? 'not-allowed' : 'unavailable', engine: 'WEB_AUDIO' })
    }
  }

  private async playNative(key: string, source: string, intent: number) {
    const audio = this.native ??= (this.dependencies.createAudio ?? (() => new Audio()))()
    audio.preload = 'none'
    const nativeSource = this.dependencies.resolveNativeSource?.(source) ?? source
    if (audio.getAttribute('src') !== nativeSource) audio.src = nativeSource
    try { audio.currentTime = this.resumePosition?.offset ?? 0 } catch { /* No metadata yet. */ }
    const current = () => !this.disposed && this.intent === intent
    audio.onended = () => {
      if (!current()) return
      this.resumePosition = null
      this.publish({ key: null, playbackState: 'idle', failureReason: null, engine: 'NATIVE' })
    }
    audio.onwaiting = () => { if (current()) this.publish({ key, playbackState: 'starting', failureReason: null, engine: 'NATIVE' }) }
    audio.onplaying = () => { if (current()) this.publish({ key, playbackState: 'playing', failureReason: null, engine: 'NATIVE' }) }
    audio.onerror = () => { if (current()) this.publish({ key, playbackState: 'failed', failureReason: 'unavailable', engine: 'NATIVE' }) }
    this.publish({ key, playbackState: 'starting', failureReason: null, engine: 'NATIVE' })
    try {
      await audio.play()
      if (current()) this.publish({ key, playbackState: audio.paused ? 'idle' : 'playing', failureReason: null, engine: 'NATIVE' })
    } catch {
      if (current()) this.publish({ key, playbackState: 'failed', failureReason: 'unavailable', engine: 'NATIVE' })
    }
  }

  dispose() {
    this.stop()
    this.disposed = true
    this.operations.forEach((operation) => operation.abort.abort())
    this.listeners.clear()
    this.cache.clear()
    this.nativeSources.clear()
    if (this.native) {
      this.native.onended = this.native.onplaying = this.native.onwaiting = this.native.onerror = null
      this.native.removeAttribute('src')
      this.native.load()
      this.native = null
    }
    void this.context?.close().catch(() => undefined)
    this.context = null
  }
}
